/**
 * Dashboard API Service
 * Handles communication with the librarian dashboard backend endpoints
 */

import apiBase from './api-base';

/**
 * Shapes mirror GET /api/v1/librarian/dashboard-overview/ (backend
 * lib_api/librarian/views.py::dashboard_overview): snake_case, per-section.
 */
export interface DashboardStatistics {
  books: { total: number; added_today: number };
  copies: {
    total: number;
    available: number;
    borrowed: number;
    added_today: number;
    utilization_rate: number;
  };
  loans: {
    active: number;
    overdue: number;
    due_today: number;
    issued_today: number;
    returned_today: number;
  };
  members: { total: number; active: number; new_today: number };
  fines: { outstanding_count: number; outstanding_amount: number; collected_today: number };
  requests: { pending: number };
  tasks: { pending: number };
  notifications: { unread: number };
}

export interface ActivityItem {
  id: string;
  type: 'book_issued' | 'book_returned';
  timestamp: string;
  description: string;
  /** Borrower display name. */
  user: string;
  icon: string;
  category: 'loan' | 'return';
  details: {
    loan_id: number;
    book_title: string;
    member_name: string;
    due_date: string | null;
  };
}

export interface DashboardTask {
  id: number;
  title: string;
  description: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  category: string;
  due_time: string | null;
  estimated_duration: string | null;
  assigned_by: string | null;
  auto_generated: boolean;
  related_books: Array<{ id: number; title: string; count?: number }>;
  created_at: string;
}

export interface DashboardNotification {
  id: number;
  title: string;
  message: string;
  notification_type: string;
  is_read: boolean;
  created_at: string;
  priority: string;
}

export interface QuickStat {
  label: string;
  value: number;
  icon: string;
  color: string;
  change: number | null;
}

export interface DashboardOverviewResponse {
  statistics: DashboardStatistics;
  recent_activity: ActivityItem[];
  daily_tasks: DashboardTask[];
  notifications: DashboardNotification[];
  quick_stats: QuickStat[];
  generated_at: string;
  user: { id: number; name: string; role: string };
}

export interface WeeklyActivityItem {
  day: string;
  issues: number;
  returns: number;
}

/**
 * Dashboard API endpoints
 */
export const dashboardApi = {
  /**
   * Consolidated dashboard overview (statistics, recent activity, tasks,
   * notifications). Library-wide sections are cached server-side for 60 s.
   */
  getDashboardOverview: async (params?: {
    refresh?: boolean;
    include_activity?: boolean;
    include_tasks?: boolean;
    include_notifications?: boolean;
    activity_limit?: number;
    tasks_limit?: number;
    notifications_limit?: number;
  }): Promise<DashboardOverviewResponse> => {
    const response = await apiBase.get<DashboardOverviewResponse>('/librarian/dashboard-overview/', { params });
    return response.data;
  },

  /**
   * Issues/returns per day for the last 7 days (oldest first).
   */
  getWeeklyActivity: (): Promise<WeeklyActivityItem[]> =>
    apiBase.get<WeeklyActivityItem[]>('/librarian/weekly-activity/').then((r) => r.data),
};

export default dashboardApi;
